import { action, makeObservable, observable } from "mobx"
import { ErrorHandler } from "../../error/ErrorHandler"
import { TableConfig } from "../../components/DataTable/types/TableConfig"
import { ViewModel } from "../ViewModel"
import { UploadTransactionsUseCase } from "../../../domain/useCases/transactions/uploadTransactions"
import { UploadProgressStatus } from "../../../domain/enum/uploadProgressStatus.enum"
import { Filter } from "../../../domain/types/transactions/Filter"
import { Transaction } from "../../../domain/entities/Transaction"
import { GetTransactionsUseCase } from "../../../domain/useCases/transactions/getTransactions"
import { DownloadTransactionsUseCase } from "../../../domain/useCases/transactions/downloadTransactions"
import { FileService } from "../../../domain/services/FileService"
import { UpdateTransactionUseCase } from "../../../domain/useCases/transactions/updateTransaction"
import { GetMicrositesUseCase } from "../../../domain/useCases/microsites/getMicrosites"
import { Microsite } from "../../../domain/entities/Microsite"
import { GetSellerServicesUseCase } from "../../../domain/useCases/sellersServices/getSellersServices"
import { SellerService } from "../../../domain/entities/SellerService"
import { GetSellerServiceDetailUseCase } from "../../../domain/useCases/sellersServices/getSellerServiceDetail"
import { GetVoucherRulesUseCase } from "../../../domain/useCases/vouchers/getVoucherRules"
import { VoucherRules } from "../../../data/dto/VoucherRules.dto"
import { CreateTransactionUseCase } from "../../../domain/useCases/transactions/createTransaction"
import { CreateTransactionDTO } from "../../../data/dto/CreateTransaction.dto"
import { SellerServiceDetail } from "../../../domain/entities/SellerServiceDetail"
import { GetGiftCardsUseCase } from "../../../domain/useCases/giftCards/getGiftCards"
import { GiftCard } from "../../../domain/entities/GiftCard"

interface Props {
	ErrorHandler: ErrorHandler
	UploadTransactionsUseCase: UploadTransactionsUseCase
	GetTransactionsUseCase: GetTransactionsUseCase
	DownloadTransactionsUseCase: DownloadTransactionsUseCase
	UpdateTransactionUseCase: UpdateTransactionUseCase
	GetMicrositesUseCase: GetMicrositesUseCase
	GetSellerServicesUseCase: GetSellerServicesUseCase
	GetSellerServiceDetailUseCase: GetSellerServiceDetailUseCase
	GetVoucherRulesUseCase: GetVoucherRulesUseCase
	CreateTransactionUseCase: CreateTransactionUseCase
	GetGiftCardsUseCase: GetGiftCardsUseCase
}
type ErrorType = "header" | "detail" | undefined
type Error = { message: string; type: ErrorType }

export class TransactionsViewModel extends ViewModel {
	private _errorHandler
	private _uploadTransactionsUseCase: UploadTransactionsUseCase
	private _createTransactionUseCase: CreateTransactionUseCase
	private _updateTransactionUseCase: UpdateTransactionUseCase
	private _getTransactionsUseCase: GetTransactionsUseCase
	private _downloadTransactionsUseCase: DownloadTransactionsUseCase
	private _getMicrositesUseCase: GetMicrositesUseCase
	private _getServicesUseCase: GetSellerServicesUseCase
	private _getServiceDetailUseCase: GetSellerServiceDetailUseCase
	private _getVoucherRulesUseCase: GetVoucherRulesUseCase
	private _getGiftCardsUseCase: GetGiftCardsUseCase
	isFetching: boolean = false
	isLoading: boolean = false
	isLoadingDetail: boolean = false
	progressData: { active: boolean; file?: File; message: string; status?: UploadProgressStatus } = {
		active: false,
		message: "",
		status: UploadProgressStatus.LOADING
	}
	uploadingFile: boolean = false
	searchedTransactions: Transaction[] = []
	searchedGiftCards: GiftCard[] = []
	error: Error = { message: "", type: undefined }
	editMode: boolean = false
	transactions: Transaction[] = []
	giftCards: GiftCard[] = []
	activeTab: "services" | "giftcards" = "services"
	searchValue?: string
	tableConfig: TableConfig = {
		pageSize: 20,
		sort: { order: "descend", field: "id" }
	}
	tipTableFilters = {}
	microsites: Microsite[] | null = null
	services: SellerService[] | null = null
	selectedService: SellerServiceDetail | null = null
	voucherRules: VoucherRules[] | null = null

	constructor({
		ErrorHandler,
		UploadTransactionsUseCase,
		GetTransactionsUseCase,
		DownloadTransactionsUseCase,
		UpdateTransactionUseCase,
		GetMicrositesUseCase,
		GetSellerServicesUseCase,
		GetSellerServiceDetailUseCase,
		GetVoucherRulesUseCase,
		CreateTransactionUseCase,
		GetGiftCardsUseCase
	}: Props) {
		super()
		makeObservable(this, {
			searchValue: observable,
			setSearchValue: action,
			uploadingFile: observable,
			setUploadingFile: action,
			progressData: observable,
			setProgressData: action,
			isFetching: observable,
			isLoading: observable,
			isLoadingDetail: observable,
			transactions: observable,
			giftCards: observable,
			activeTab: observable,
			microsites: observable,
			services: observable,
			selectedService: observable,
			voucherRules: observable,
			setTransactions: action,
			setGiftCards: action,
			setActiveTab: action,
			searchedTransactions: observable,
			searchedGiftCards: observable,
			setSearchedTransactions: action,
			setSearchedGiftCards: action,
			setMicrosites: action,
			setServices: action,
			setSelectedService: action,
			setLoading: action,
			setLoadingDetail: action,
			cleanPagination: action,
			setVoucherRules: action
		})
		this._errorHandler = ErrorHandler
		this._uploadTransactionsUseCase = UploadTransactionsUseCase
		this._getTransactionsUseCase = GetTransactionsUseCase
		this._downloadTransactionsUseCase = DownloadTransactionsUseCase
		this._updateTransactionUseCase = UpdateTransactionUseCase
		this._getMicrositesUseCase = GetMicrositesUseCase
		this._getServicesUseCase = GetSellerServicesUseCase
		this._getServiceDetailUseCase = GetSellerServiceDetailUseCase
		this._getVoucherRulesUseCase = GetVoucherRulesUseCase
		this._createTransactionUseCase = CreateTransactionUseCase
		this._getGiftCardsUseCase = GetGiftCardsUseCase
		this.fetchTransactions()
	}

	public async uploadTransactions(file: File) {
		try {
			this.setUploadingFile(true)
			const frmData = new FormData()
			frmData.append("file", file)
			const response = await this._uploadTransactionsUseCase.exec(frmData)
			return response
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setUploadingFile(false)
		}
	}

	public async createTransaction(transaction: CreateTransactionDTO) {
		try {
			const transactionId = await this._createTransactionUseCase.exec(transaction)
			this.fetchTransactions()
			return transactionId
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async updateTransaction(transaction: Transaction, sendEmails: boolean) {
		try {
			await this._updateTransactionUseCase.exec({ transaction, sendEmails: sendEmails ?? false })
			this.fetchTransactions()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public closeProgress() {
		this.setProgressData({ active: false, message: "", status: UploadProgressStatus.LOADING })
	}

	public async fetchTransactions(filter?: Filter) {
		try {
			this.setLoading(true)

			// Fetch transactions
			const transactions = await this._getTransactionsUseCase.exec({
				pagination: 0,
				limit: this.limit,
				filter: { ...filter, search: this.searchValue }
			})

			// Sort transactions by date (most recent first)
			const sortedTransactions = transactions.sort((a, b) => {
				return new Date(b.requestDate).getTime() - new Date(a.requestDate).getTime()
			})

			// Try to fetch gift cards, but don't fail if the endpoint doesn't exist yet
			let giftCardTransactions: GiftCard[] = []
			try {
				const giftCards = await this._getGiftCardsUseCase.exec({
					pagination: 0,
					limit: this.limit,
					filter: { ...filter, search: this.searchValue }
				})
				giftCardTransactions = giftCards.sort((a, b) => {
					return new Date(b.date).getTime() - new Date(a.date).getTime()
				})
			} catch (giftCardError) {
				console.warn("Gift cards endpoint not available yet:", giftCardError)
			}

			this.setTransactions(sortedTransactions)
			this.setGiftCards(giftCardTransactions)
			this.cleanPagination()
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async downloadTransactions(filter?: Filter) {
		try {
			this.setLoading(true)
			const file = await this._downloadTransactionsUseCase.exec({
				pagination: 0,
				limit: 5000,
				filter: { ...filter, search: this.searchValue }
			})
			const fileService = new FileService()
			const dateString = new Date().toISOString().split("T")[0]
			fileService.dowloadFile(file, `Transactions_${dateString}.csv`)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async fetchMicrositesAndServices() {
		try {
			this.setLoading(true)
			const microsites = await this._getMicrositesUseCase.exec()
			const services = await this._getServicesUseCase.exec({ pagination: 0, limit: 1000, status: "active" })
			this.setMicrosites(microsites)
			this.setServices(services)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	public async fetchServiceDetail(serviceId: number, filter?: { micrositeId?: number; country?: string }) {
		try {
			this.setLoadingDetail(true)
			const serviceDetail = await this._getServiceDetailUseCase.exec(serviceId.toString())
			const voucherRules = await this._getVoucherRulesUseCase.exec({ serviceId, ...filter })
			this.setVoucherRules(voucherRules)
			this.setSelectedService(serviceDetail)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoadingDetail(false)
		}
	}

	public async fetchVoucherRules(filter?: { serviceId?: number; micrositeId?: number; country?: string }) {
		try {
			this.setLoadingDetail(true)
			const voucherRules = await this._getVoucherRulesUseCase.exec(filter ?? {})
			this.setVoucherRules(voucherRules)
		} catch (error: any) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoadingDetail(false)
		}
	}

	public cleanPagination() {
		this.pagination = 0
		this.setHasNextPage(true)
	}

	public async fetchPaginatedTransactions(filter?: Filter) {
		this.setIsFetching(true)
		try {
			// Fetch transactions
			const transactions = await this._getTransactionsUseCase.exec({
				pagination: this.pagination,
				limit: this.limit,
				filter: { ...filter, search: this.searchValue }
			})

			// Try to fetch gift cards, but don't fail if the endpoint doesn't exist yet
			let giftCardTransactions: GiftCard[] = []
			try {
				const giftCards = await this._getGiftCardsUseCase.exec({
					pagination: this.pagination,
					limit: this.limit,
					filter: { ...filter, search: this.searchValue }
				})
				giftCardTransactions = giftCards
			} catch (giftCardError) {
				console.warn("Gift cards endpoint not available yet:", giftCardError)
			}

			if (transactions.length > 0) {
				const combinedTransactions = [...transactions, ...this.transactions]
				this.setTransactions(combinedTransactions)
			}

			if (giftCardTransactions.length > 0) {
				const combinedGiftCards = [...giftCardTransactions, ...this.giftCards]
				this.setGiftCards(combinedGiftCards)
			}

			this.setHasNextPage(transactions.length > 0 || giftCardTransactions.length > 0)
		} catch (error) {
			throw this._errorHandler.handleError(error)
		} finally {
			this.setIsFetching(false)
		}
	}

	public async fetchGiftCards(filter?: any) {
		try {
			this.setLoading(true)

			// Build filter params specific to gift cards
			const giftCardFilter: any = {}

			if (filter?.createdAt) {
				giftCardFilter.dateFrom = filter.createdAt[0].format("YYYY-MM-DD")
				giftCardFilter.dateTo = filter.createdAt[1].format("YYYY-MM-DD")
			}

			if (filter?.movementType) {
				giftCardFilter.movementType = filter.movementType
			}

			if (filter?.search || this.searchValue) {
				giftCardFilter.search = filter?.search || this.searchValue
			}

			const giftCards = await this._getGiftCardsUseCase.exec({
				pagination: 0,
				limit: this.limit,
				filter: giftCardFilter
			})

			const sortedGiftCards = giftCards.sort((a, b) => {
				return new Date(b.date).getTime() - new Date(a.date).getTime()
			})

			this.setGiftCards(sortedGiftCards)
			this.cleanPagination()
		} catch (error: any) {
			console.error("Error fetching gift cards:", error)
			throw this._errorHandler.handleError(error)
		} finally {
			this.setLoading(false)
		}
	}

	// ACTIONS
	setSearchValue(value?: string) {
		this.searchValue = value
	}

	setTransactions(transactions: Transaction[]) {
		this.transactions = transactions
		if (this.activeTab === "services") {
			this.setSearchedTransactions(transactions)
		}
	}

	setGiftCards(giftCards: GiftCard[]) {
		this.giftCards = giftCards
		if (this.activeTab === "giftcards") {
			this.setSearchedGiftCards(giftCards)
		}
	}

	setActiveTab(tab: "services" | "giftcards") {
		this.activeTab = tab
		if (tab === "services") {
			this.setSearchedTransactions(this.transactions)
		} else {
			this.setSearchedGiftCards(this.giftCards)
		}
	}

	setLoading(isLoading: boolean) {
		this.isLoading = isLoading
	}

	setLoadingDetail(isLoadingDetail: boolean) {
		this.isLoadingDetail = isLoadingDetail
	}

	setUploadingFile(uploadingFile: boolean) {
		this.uploadingFile = uploadingFile
	}

	setIsFetching(isFetching: boolean) {
		this.isFetching = isFetching
	}

	setProgressData(progressData: typeof this.progressData) {
		this.progressData = progressData
	}

	setEditMode(editMode: boolean) {
		this.editMode = editMode
	}

	setSearchedTransactions(transactions: Transaction[]) {
		this.searchedTransactions = transactions
	}

	setSearchedGiftCards(giftCards: GiftCard[]) {
		this.searchedGiftCards = giftCards
	}

	setError(message: string, type: ErrorType) {
		this.error = { message, type }
	}

	setTableConfig(config: Partial<typeof this.tableConfig>) {
		this.tableConfig = { ...this.tableConfig, ...config }
	}

	setMicrosites(microsites: Microsite[]) {
		this.microsites = microsites
	}

	setServices(services: SellerService[]) {
		this.services = services
	}
	setSelectedService(service: SellerServiceDetail) {
		this.selectedService = service
	}
	setVoucherRules(voucherRules: VoucherRules[]) {
		this.voucherRules = voucherRules
	}
}
